import Swal, { SweetAlertResult } from 'sweetalert2';
import {
  ConfirmDialogConfig,
  NotificationConfig,
  DeleteConfirmConfig,
  DialogResult
} from '../types/dialog.types';
import { DIALOG_COLORS, DIALOG_MESSAGES, DEFAULT_TIMER } from '../utils/dialogConfig';
import '../styles/dialog.css';

export const useConfirmDialog = () => {

  /**
   * Generic confirmation dialog
   */
  const confirm = async (config: ConfirmDialogConfig): Promise<DialogResult> => {
    const result = await Swal.fire({
      title: config.title,
      text: config.html ? undefined : config.text, // text is ignored if html is provided
      html: config.html,
      icon: config.icon,
      showCancelButton: true,
      confirmButtonColor: config.confirmButton?.color || DIALOG_COLORS.confirm.primary,
      cancelButtonColor: config.cancelButton?.color || DIALOG_COLORS.cancel.default,
      confirmButtonText: config.confirmButton?.text || 'Yes',
      cancelButtonText: config.cancelButton?.text || 'Cancel',
      reverseButtons: config.reverseButtons ?? true,
    });

    return {
      isConfirmed: result.isConfirmed,
      isDenied: result.isDenied,
      isDismissed: result.isDismissed,
      value: result.value,
    };
  };

  /**
   * Delete/Deactivate confirmation with standardized messaging
   * Uses warning icon and red confirm button to match existing UI patterns
   * @param config.isSoftDelete - When true, uses "Deactivate" instead of "Delete"
   */
  const confirmDelete = async (config: DeleteConfirmConfig): Promise<boolean> => {
    const messages = config.isSoftDelete ? DIALOG_MESSAGES.deactivate : DIALOG_MESSAGES.delete;

    const result = await Swal.fire({
      title: config.customTitle || messages.title,
      text: config.customHtml ? undefined : (config.customText || messages.text(config.itemName, config.itemType)),
      html: config.customHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: DIALOG_COLORS.confirm.danger,
      cancelButtonColor: DIALOG_COLORS.cancel.default,
      confirmButtonText: messages.confirmText,
      cancelButtonText: messages.cancelText,
      reverseButtons: true,
      customClass: {
        popup: 'swal-compact',
      },
    });

    return result.isConfirmed;
  };

  /**
   * Success notification (auto-closes by default)
   */
  const notifySuccess = (
    title: string = DIALOG_MESSAGES.save.successTitle,
    text?: string,
    options?: Partial<NotificationConfig>
  ): Promise<SweetAlertResult> => {
    return Swal.fire({
      icon: 'success',
      title,
      text: options?.html ? undefined : (text || DIALOG_MESSAGES.save.successText),
      html: options?.html,
      confirmButtonColor: DIALOG_COLORS.confirm.success,
      timer: options?.autoClose !== false ? (options?.duration || DEFAULT_TIMER) : undefined,
      showConfirmButton: options?.autoClose === false,
    });
  };

  /**
   * Error notification (does not auto-close by default)
   */
  const notifyError = (
    title: string = DIALOG_MESSAGES.save.errorTitle,
    text?: string,
    options?: Partial<NotificationConfig>
  ): Promise<SweetAlertResult> => {
    return Swal.fire({
      icon: 'error',
      title,
      text: options?.html ? undefined : (text || DIALOG_MESSAGES.save.errorText),
      html: options?.html,
      confirmButtonColor: DIALOG_COLORS.confirm.danger,
      timer: options?.autoClose ? (options?.duration || DEFAULT_TIMER) : undefined,
      showConfirmButton: options?.autoClose !== true,
    });
  };

  /**
   * Warning notification
   */
  const notifyWarning = (
    title: string,
    text?: string,
    options?: Partial<NotificationConfig>
  ): Promise<SweetAlertResult> => {
    return Swal.fire({
      icon: 'warning',
      title,
      text: options?.html ? undefined : text,
      html: options?.html,
      confirmButtonColor: DIALOG_COLORS.confirm.warning,
      showConfirmButton: true,
    });
  };

  /**
   * Info notification
   */
  const notifyInfo = (
    title: string,
    text?: string,
    options?: Partial<NotificationConfig>
  ): Promise<SweetAlertResult> => {
    return Swal.fire({
      icon: 'info',
      title,
      text: options?.html ? undefined : text,
      html: options?.html,
      confirmButtonColor: DIALOG_COLORS.confirm.primary,
    });
  };

  /**
   * Delete/Deactivate success notification (standardized)
   * @param itemType - The type of item being deleted/deactivated
   * @param isSoftDelete - When true, uses "Deactivated" instead of "Deleted"
   */
  const notifyDeleteSuccess = (itemType?: string, isSoftDelete?: boolean): Promise<SweetAlertResult> => {
    const messages = isSoftDelete ? DIALOG_MESSAGES.deactivate : DIALOG_MESSAGES.delete;
    return Swal.fire({
      icon: 'success',
      title: messages.successTitle,
      text: messages.successText(itemType),
      confirmButtonColor: DIALOG_COLORS.confirm.success,
      timer: DEFAULT_TIMER,
      showConfirmButton: false,
    });
  };

  /**
   * Delete/Deactivate error notification (standardized)
   * @param customText - Custom error message
   * @param isSoftDelete - When true, uses "Deactivate Failed" instead of "Delete Failed"
   */
  const notifyDeleteError = (customText?: string, isSoftDelete?: boolean): Promise<SweetAlertResult> => {
    const messages = isSoftDelete ? DIALOG_MESSAGES.deactivate : DIALOG_MESSAGES.delete;
    return Swal.fire({
      icon: 'error',
      title: messages.errorTitle,
      text: customText || messages.errorText,
      confirmButtonColor: DIALOG_COLORS.confirm.danger,
    });
  };

  return {
    // Confirmations
    confirm,
    confirmDelete,

    // Notifications
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,

    // Specialized
    notifyDeleteSuccess,
    notifyDeleteError,

    // Direct access for custom cases
    Swal,
  };
};

// Export standalone functions for use outside React components
export const dialogUtils = {
  confirmDelete: async (config: DeleteConfirmConfig): Promise<boolean> => {
    const messages = config.isSoftDelete ? DIALOG_MESSAGES.deactivate : DIALOG_MESSAGES.delete;
    const result = await Swal.fire({
      title: config.customTitle || messages.title,
      text: config.customHtml ? undefined : (config.customText || messages.text(config.itemName, config.itemType)),
      html: config.customHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: DIALOG_COLORS.confirm.danger,
      cancelButtonColor: DIALOG_COLORS.cancel.default,
      confirmButtonText: messages.confirmText,
      reverseButtons: true,
      customClass: {
        popup: 'swal-compact',
      },
    });
    return result.isConfirmed;
  },

  notifySuccess: (title: string, text?: string, html?: string) => {
    return Swal.fire({
      icon: 'success',
      title,
      text: html ? undefined : text,
      html,
      confirmButtonColor: DIALOG_COLORS.confirm.success,
      timer: DEFAULT_TIMER,
      showConfirmButton: false,
    });
  },

  notifyError: (title: string, text?: string, html?: string) => {
    return Swal.fire({
      icon: 'error',
      title,
      text: html ? undefined : text,
      html,
      confirmButtonColor: DIALOG_COLORS.confirm.danger,
    });
  },

  notifyWarning: (title: string, text?: string, html?: string) => {
    return Swal.fire({
      icon: 'warning',
      title,
      text: html ? undefined : text,
      html,
      confirmButtonColor: DIALOG_COLORS.confirm.warning,
      showConfirmButton: true,
    });
  },

  notifyInfo: (title: string, text?: string, html?: string) => {
    return Swal.fire({
      icon: 'info',
      title,
      text: html ? undefined : text,
      html,
      confirmButtonColor: DIALOG_COLORS.confirm.primary,
    });
  },
};

export default useConfirmDialog;
